import React, { useEffect, useMemo } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { useTenant } from '@emerald-works-nova/auth'
import Retention from './pages/profile/components/subscription-and-billing/components/retention'
import { Box, CircularProgress } from '@mui/material'
import PrivateRoute from './components/private-route'
import { AppLoading } from './components'
import { useSession } from '@emerald-works/react-auth'
import useSessionInactivity from './hooks/useSessionInactivity'

const PrimaryLayout = React.lazy(() => import('./layouts/primary-layout'))
const SecondaryLayout = React.lazy(() => import('./layouts/secondary-layout'))
const AuthNoLayout = React.lazy(() => import('./layouts/auth-no-layout'))
const PrivacyPolicyPage = React.lazy(() => import('./pages/policies/privacy'))
const TandCPage = React.lazy(() => import('./pages/policies/tandc'))
const CookiesPolicyPage = React.lazy(() => import('./pages/policies/cookies'))
const BookmarksPage = React.lazy(() => import('./pages/bookmarks'))
const SignUpPage = React.lazy(() => import('./pages/sign-up'))
const SignInPage = React.lazy(() => import('./pages/sign-in'))
const VerifyEmailPage = React.lazy(() => import('./pages/verify-email'))
const VerifyEmailResendPage = React.lazy(() => import('./pages/resend-email'))
const PasswordForceChangePage = React.lazy(() => import('./pages/password-force-change'))
const PasswordRecoveryPage = React.lazy(() => import('./pages/password-recovery'))
const PasswordRecoveredPage = React.lazy(() => import('./pages/password-recovered'))
const PasswordResetPage = React.lazy(() => import('./pages/password-reset'))
const PasswordResetEndPage = React.lazy(() => import('./pages/password-reset-end'))
const SearchResultsPage = React.lazy(() => import('./pages/search-results'))
const PlaylistLanding = React.lazy(() => import('./pages/playlists'))
const PlaylistView = React.lazy(() => import('./pages/playlist-detail'))
const Home = React.lazy(() => import('./pages/home'))
const ProfilePage = React.lazy(() => import('./pages/profile'))
const NewestReleasesPage = React.lazy(() => import('./pages/newest-releases'))
const MostPopularPage = React.lazy(() => import('./pages/most-popular'))
const NotFoundPage = React.lazy(() => import('./pages/not-found'))
const MaintenancePage = React.lazy(() => import('./pages/maintenance'))
const B2CSignUp = React.lazy(() => import('./pages/b2c-signup'))
const B2CSignupSuccess = React.lazy(() => import('./pages/b2c-signup-success'))
const B2CSocialRedict = React.lazy(() => import('./pages/b2c-social-redirect'))
const Location = React.lazy(() => import('./pages/location'))
const BonusMaterialPage = React.lazy(() => import('./pages/bonus-material'))
const A11yStatementPage = React.lazy(() => import('./pages/policies/a11y-statement'))
const SkillBiteSessionPage = React.lazy(() => import('./pages/skill-bite-session'))
const SkillBitesPage = React.lazy(() => import('./pages/skill-bites'))
const SkillBiteDetailPage = React.lazy(() => import('./pages/skill-bite-detail'))

const RoutingLazyLoading = () => (
  <Box display='flex' flex={1} justifyContent='center' alignContent='center' p={10}>
    <CircularProgress size={75} />
  </Box>
)

const ScrollToTop = ({ children }) => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return children
}

export const routes = {
  article: {
    path: '/:locationId/*',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <Location />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary'
  },
  register: {
    path: '/signup',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <SignUpPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'secondary',
    public: true
  },
  b2cFreeRegister: {
    path: '/signup/free',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <B2CSignUp isFree />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  b2cRegister: {
    path: '/signup/:plan',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <B2CSignUp />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  b2cSocialSignUpRedirect: {
    path: '/social-signup/redirect/:plan',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <B2CSocialRedict />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  b2cRegisterSuccess: {
    path: '/b2c/sign-up/success/:username/:planCode',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <B2CSignupSuccess />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  b2cFreeRegisterSuccess: {
    path: '/b2c/sign-up/success/free',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <B2CSignupSuccess isFree />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  login: {
    path: '/login',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <SignInPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'secondary',
    public: true
  },
  'verify-email': {
    path: '/verify-email/:username/:code',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <VerifyEmailPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  'resend-email': {
    path: '/verify-email',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <VerifyEmailResendPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  'recovery-password': {
    path: '/password-recovery',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PasswordRecoveryPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'secondary',
    public: true
  },
  'recovered-password': {
    path: '/password-recovered',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PasswordRecoveredPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  'reset-password-end': {
    path: '/password-reset-end',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PasswordResetEndPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  'reset-password': {
    path: '/password-reset/:username/:code',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PasswordResetPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  'change-password': {
    path: '/change-password',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PasswordForceChangePage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  bookmarks: {
    path: '/bookmarks',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <BookmarksPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|free|suspended|ew|editor|cx/
  },
  serp: {
    path: '/search-results',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <SearchResultsPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: true
  },
  profile: {
    path: '/profile',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <ProfilePage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false
  },
  retention: {
    path: '/profile/retention',
    element: <Retention />,
    layout: 'primary',
    allowedUserGroups: /consumer|free|suspended/,
    public: false
  },
  playlists: {
    path: '/playlists',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PlaylistLanding />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  playlist: {
    path: '/playlists/:playlistId',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PlaylistView />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  'playlist-edit': {
    path: '/playlists/:playlistId/edit',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PlaylistView edit />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  'playlist-create': {
    path: '/playlists/new',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PlaylistView create />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  'skill-bites': {
    path: '/skill-bites',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <SkillBitesPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  'skill-bite': {
    path: '/skill-bites/:skillBiteId/',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <SkillBiteDetailPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  'skill-bite-session': {
    path: '/skill-bites/:skillBiteId/:sessionId/',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <SkillBiteSessionPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false,
    allowedUserGroups: /clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/
  },
  'privacy-policy': {
    path: '/policies/privacy',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <PrivacyPolicyPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: true
  },
  'cookie-policy': {
    path: '/policies/cookie',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <CookiesPolicyPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true,
    layout: 'primary'
  },
  tandc: {
    path: '/policies/tandc',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <TandCPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: true
  },
  'a11y-statement': {
    path: '/policies/a11y',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <A11yStatementPage />
        </ScrollToTop>
      </React.Suspense>),
    layout: 'primary',
    public: true
  },
  'newest-releases': {
    path: '/newest-releases',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <NewestReleasesPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: true
  },
  'most-popular': {
    path: '/most-popular',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <MostPopularPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: true
  },
  maintenance: {
    path: 'maintenance',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <MaintenancePage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  },
  'bonus-material': {
    path: '/bonus-material',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <BonusMaterialPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: false
  },
  home: {
    path: '/home',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <Home />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'auth',
    public: false
  },
  index: {
    path: '/',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <Navigate to='/home' replace />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'auth',
    public: true
  },
  indexHtml: {
    path: '/index.html',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <Home />
        </ScrollToTop>
      </React.Suspense>
    ),
    layout: 'primary',
    public: true
  },
  '*': {
    path: '*',
    element: (
      <React.Suspense fallback={<RoutingLazyLoading />}>
        <ScrollToTop>
          <NotFoundPage />
        </ScrollToTop>
      </React.Suspense>
    ),
    public: true
  }
}

const ApplicationRoutes = () => {
  useSessionInactivity()
  const tenant = useTenant()
  const { isAuthenticated } = useSession()
  const normalizedRoutesNoLayout = useMemo(() => {
    return Object.entries(routes)
      .filter(([_, definition], idx) => !definition.layout)
      .map(([_, definition], idx) => {
        return <Route {...definition} key={idx} />
      })
  }, [])

  const normalizedRoutesPrimaryLayout = useMemo(() => {
    return Object.entries(routes)
      .filter(([_, definition], idx) => definition?.layout === 'primary' || (isAuthenticated && definition?.layout === 'auth'))
      .map(([_, definition], idx) => {
        const openRoute = definition.public || (tenant.isPublicTenant && definition.public !== false)
        if (openRoute) {
          return <Route {...definition} key={idx} />
        } else {
          const { path, ...rest } = definition
          return <Route path={path} key={idx} element={<PrivateRoute {...rest} />} />
        }
      })
  }, [tenant, isAuthenticated])

  const normalizedRoutesSecondaryLayout = useMemo(() => {
    return Object.entries(routes)
      .filter(([_, definition], idx) => definition?.layout === 'secondary')
      .map(([_, definition], idx) => {
        const openRoute = definition.public || (tenant.isPublicTenant && definition.public !== false)
        if (openRoute) {
          return <Route {...definition} key={idx} />
        } else {
          const { path, ...rest } = definition
          return <Route path={path} key={idx} element={<PrivateRoute {...rest} />} />
        }
      })
  }, [tenant])

  const authRoutesNoLayout = useMemo(() => {
    return Object.entries(routes)
      .filter(([_, definition], idx) => !isAuthenticated && definition.layout === 'auth')
      .map(([_, definition], idx) => {
        const openRoute = definition.public || (tenant.isPublicTenant && definition.public !== false)
        if (openRoute) {
          return <Route {...definition} key={idx} />
        } else {
          const { path, ...rest } = definition
          return <Route path={path} key={idx} element={<PrivateRoute {...rest} />} />
        }
      })
  }, [tenant, isAuthenticated])

  return (
    <React.Suspense fallback={<AppLoading />}>
      <Routes>
        <Route element={<PrimaryLayout />}>{normalizedRoutesPrimaryLayout}</Route>
        <Route element={<SecondaryLayout />}>{normalizedRoutesSecondaryLayout}</Route>
        <Route element={<AuthNoLayout />}>{authRoutesNoLayout}</Route>
        {normalizedRoutesNoLayout}
      </Routes>
    </React.Suspense>
  )
}

export default ApplicationRoutes
